import Link from 'next/link';

import { useState } from 'react';
import Button from '@components/atoms/button';
import Select from '@components/atoms/Select';
import FFHModal from '@components/modal';
import { BodyRegular, H2 } from '@components/atoms/typography';
import { CHIP_CONTENT } from '@lib/my-team/utils/chip-strategy';
import type { ChipType } from '@api-service/planner';

const SELECT_MENU_MAX_HEIGHT = 180;

/**
 * The option that unschedules the chip. It is a sentinel rather than a real
 * gameweek so that "chose to clear the chip" stays distinct from "has not
 * chosen anything yet", which both have to leave the save button in a
 * different state.
 */
const NOT_SET_VALUE = 'NOT_SET';
const NOT_SET_OPTION = { value: NOT_SET_VALUE, label: 'Not set' };

type ChipWeekChoice = number | typeof NOT_SET_VALUE;

type ChipModalContentProps = {
  chipType: ChipType;
  /** The gameweek the chip is currently scheduled for, if any. */
  scheduledGameweek: number | null;
  gameweekOptions: number[];
  isPending: boolean;
  onClose: () => void;
  /** `null` unschedules the chip, returning it to "not set". */
  onSave: (gameweek: number | null) => void;
};

const toSelectOption = (gameweek: number) => ({
  value: gameweek,
  label: `GW${gameweek}`
});

const ChipModalContent = ({
  chipType,
  scheduledGameweek,
  gameweekOptions,
  isPending,
  onClose,
  onSave
}: ChipModalContentProps) => {
  // Only preselect the scheduled gameweek while it is still on offer; a
  // stale, now-invalid selection must be re-chosen, not silently resubmitted.
  const [chosenGameweek, setChosenGameweek] = useState<ChipWeekChoice | null>(
    scheduledGameweek !== null && gameweekOptions.includes(scheduledGameweek)
      ? scheduledGameweek
      : null
  );
  const content = CHIP_CONTENT[chipType];
  const canSave = chosenGameweek !== null && !isPending;
  // Clearing is only offered once there is a week to clear, mirroring the
  // reset control this replaces. Offering it on an unset chip would put a
  // no-op save in front of the user.
  const selectOptions = [
    ...(scheduledGameweek !== null ? [NOT_SET_OPTION] : []),
    ...gameweekOptions.map(toSelectOption)
  ];

  return (
    <div
      className="flex flex-col gap-6 p-6 w-full md:w-[420px]"
      data-cy="chip-modal"
    >
      <div className="flex flex-col gap-1">
        <H2>{content.label}</H2>
        <BodyRegular textColor="text-neutral-600">
          {content.description}{' '}
          <span className="text-primary-600 underline">
            <Link href={content.guideUrl}>Read the {content.label} guide</Link>
          </span>
          .
        </BodyRegular>
      </div>

      <div className="flex flex-col gap-2">
        <BodyRegular textColor="text-neutral-600">
          Choose the gameweek you want to play this chip in.
        </BodyRegular>
        <Select
          name={`${chipType.toLowerCase()}-chip-select`}
          value={
            chosenGameweek === null
              ? null
              : chosenGameweek === NOT_SET_VALUE
              ? NOT_SET_OPTION
              : toSelectOption(chosenGameweek)
          }
          options={selectOptions}
          placeholder="Choose gameweek"
          maxMenuHeight={SELECT_MENU_MAX_HEIGHT}
          isDisabled={isPending}
          onChange={(option) => setChosenGameweek(option.value)}
          className="w-full md:w-[300px]"
          dataCy="chip-modal-gameweek-select"
        />
      </div>

      <div className="flex justify-end gap-3">
        <Button
          variant="secondary"
          onClick={onClose}
          data-cy="chip-modal-cancel"
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            if (chosenGameweek === null) return;
            onSave(chosenGameweek === NOT_SET_VALUE ? null : chosenGameweek);
          }}
          disabled={!canSave}
          data-cy="chip-modal-confirm"
        >
          {isPending ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </div>
  );
};

type ChipModalProps = Omit<ChipModalContentProps, 'chipType'> & {
  chipType: ChipType | null;
};

export const ChipModal = ({ chipType, ...contentProps }: ChipModalProps) => {
  if (!chipType) return null;

  return (
    <FFHModal
      open
      handleClose={contentProps.onClose}
      title={`${CHIP_CONTENT[chipType].label} chip`}
      allowOverflow
    >
      {/* Keyed so switching chip resets the locally chosen gameweek. */}
      <ChipModalContent key={chipType} chipType={chipType} {...contentProps} />
    </FFHModal>
  );
};
